import React, { type FC, forwardRef, type MouseEvent, type Ref } from 'react';

import IconUserDefault from '@assets/img/user-ava.svg';

import type { AvatarProps } from './types';
import { getInitials, stringToColor } from './utlis';

import styles from './styles.module.scss';

/**
 * Компонент Avatar отображает аватар пользователя или группы.
 */
const AvatarBase: FC<AvatarProps> = ({
	image,
	fallbackSvg,
	size = 40,
	status = 'offline',
	className,
	onClick,
	innerRef,
	name,
	...restProps
}) => {
	const isClickable = Boolean(onClick);
	const avatarSizeClass = `size-${size}` as keyof typeof styles;
	const classNames: string[] = [styles.Avatar, styles[avatarSizeClass]];
	const indicatorClassNames: string[] = [styles.Indicator, styles[status], styles[avatarSizeClass]];
	const containerClassName = [styles.AvatarContainer, className];

	if (isClickable) {
		containerClassName.push(styles.clickable);
	}

	if (name) {
		classNames.push(styles.NameContainer);
	}

	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		if (onClick) {
			onClick(e);
		} else {
			e.preventDefault();
		}
	};

	let avatarContent = (
		<div
			className={classNames.join(' ')}
			dangerouslySetInnerHTML={image ? undefined : { __html: IconUserDefault }}
			style={image ? { backgroundImage: `url('${image}')` } : undefined}
		/>
	);

	if (fallbackSvg) {
		avatarContent = (
			<div className={classNames.join(' ')} dangerouslySetInnerHTML={{ __html: fallbackSvg }} />
		);
	} else if (name)
		avatarContent = (
			<div className={classNames.join(' ')} style={{ backgroundColor: stringToColor(name) }}>
				{getInitials(name)}
			</div>
		);

	const indicator = <div className={indicatorClassNames.join(' ')} />;

	return (
		<div
			ref={innerRef}
			className={containerClassName.join(' ')}
			tabIndex={Number(isClickable)}
			onClick={handleClick}
			{...restProps}
		>
			{avatarContent}
			{indicator}
		</div>
	);
};

export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
	(props, ref: Ref<HTMLDivElement> | null) => <AvatarBase innerRef={ref} {...props} />
);
